import { getProjectData_API, patchProjectData_API } from 'api'
import { TAppDispatch, TGetState } from 'store/types'
import { IProjectData } from 'types/projectData'
import slice from 'store/features/projectData/slice'
import { projectDataMediaThunks } from 'store/features/projectData/thunks/media'
import { projectDataAudioThunks } from 'store/features/projectData/thunks/audio'
import { sanitizeProjectData } from 'utils/projectData'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import {
  isItemAvailableToSplit,
  canCopyMediaItem,
  canCopyAudioItem
} from 'utils/timeline'
import { isAudioItem } from 'utils/audio'
import { isAiVideoPending, isVisualMediaItem } from 'utils/media'
import { IMediaItem, MediaStatus } from 'types/media'
import { isBlobUrl } from 'utils'
import {
  getTimeLineItemStartTime,
  isTimelineItemOverlappedWithPlayhead
} from './utils'
import { removeFromLocalStorageProcessingItems } from 'utils/localStorage'
import { withAppThunks } from 'store/with-app-thunks'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import { handleApiError } from 'store/utils'
import { projectDataHistoryThunks } from './history'
import { projectDataProcessingItemsThunks } from './processingItems'
import { editorThunks } from 'store/features/editor/thunks'

const { actions } = slice

const getProjectData = (id: string) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    try {
      dispatch(actions.setIsLoading(true))
      const response = await getProjectData_API(id)
      dispatch(actions.setProjectData(response.data))
      dispatch(appThunks.audioThunks.getUsedLibSounds())
      dispatch(projectDataMediaThunks.syncPendingAIVideoData())
      dispatch(projectDataMediaThunks.syncPendingAiImageData())
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setIsLoading(false))
    }
  })

const patchProjectData =
  (id: string, data: IProjectData) => async (dispatch: TAppDispatch) => {
    try {
      dispatch(actions.setIsLoading(true))
      const sanitizedData = sanitizeProjectData(data)
      const response = await patchProjectData_API(id, sanitizedData)
      return response.data
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setIsLoading(false))
    }
  }

// Split logic is common for audio and visual media items
const splitTimelineItem =
  (splitTime: number) => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const activeTimelineItem = editorSelectors.activeTimelineItem(state)
    const visualMediaItems = projectDataSelectors.mediaItems(state)
    const allAudioItems = projectDataSelectors.multilineAudios(state)
    const visualMediaItemsWithReadyStatus = visualMediaItems.filter(
      ({ meta }) => meta.status === MediaStatus.OK
    )
    const nonTtsItems = allAudioItems.filter(
      audioItem => audioItem.type !== 'TTS'
    )

    const allItems = [
      ...visualMediaItemsWithReadyStatus,
      ...nonTtsItems
    ].filter(item =>
      'meta' in item ? !isBlobUrl(item.meta.path) : !isBlobUrl(item.mediaPath)
    )

    const overlappingItems = allItems.filter(item =>
      isTimelineItemOverlappedWithPlayhead(item, splitTime)
    )

    // take active item if it is one of the overlapping items
    const activeOverlappingItem = overlappingItems.find(
      item =>
        item.id === activeTimelineItem.id &&
        item.type === activeTimelineItem.type
    )

    // move active overlapping item if it exists to the top of the overlapping items array
    if (activeOverlappingItem) {
      overlappingItems.splice(
        overlappingItems.indexOf(activeOverlappingItem),
        1
      )
      overlappingItems.unshift(activeOverlappingItem)
    }

    // find the first item that is available to split
    const splitTargetIndex = overlappingItems.findIndex(item =>
      isItemAvailableToSplit({
        itemStartTime: getTimeLineItemStartTime(item),
        itemDuration: item.duration,
        playheadTime: splitTime
      })
    )

    const splittingItem = overlappingItems[splitTargetIndex]

    if (!splittingItem) return

    if (isVisualMediaItem(splittingItem.type)) {
      if (isAiVideoPending(splittingItem as IMediaItem)) {
        return
      }
      dispatch(
        actions.splitVisualMediaItem({
          splitTime,
          splittingMediaId: splittingItem.id
        })
      )
    } else if (isAudioItem(splittingItem.type)) {
      dispatch(
        actions.splitAudioItem({
          splitTime,
          splittingAudioId: splittingItem.id
        })
      )
    }
  }

const removeTimelineSelectedItems = () =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const activeTimelineItems = editorSelectors.activeTimelineItems(state)
    const activeTimelineItemsIds = editorSelectors.activeTimelineItemsIds(state)
    dispatch(actions.removeTimelineSelectedItems(activeTimelineItems))
    dispatch(appThunks.editorThunks.setActiveTimelineItems([]))
    dispatch(appThunks.editorThunks.setAsideOverlayContent(null))
    removeFromLocalStorageProcessingItems(activeTimelineItemsIds)
    dispatch(actions.removeProcessingItems(activeTimelineItemsIds))
    dispatch(appThunks.aiImageThunks.resetGenerateForm())
    dispatch(appThunks.aiVideoThunks.clearGenerationParams())
    dispatch(appThunks.editorThunks.setToDefaultTab())
  })

const copyTimelineItem = () =>
  withAppThunks(_appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const activeMediaItem = projectDataSelectors.activeMedia(state)
    const activeAudioItem = projectDataSelectors.activeAudio(state)

    if (activeMediaItem && canCopyMediaItem(activeMediaItem)) {
      dispatch(editorThunks.setClipboard(activeMediaItem))
    } else if (activeAudioItem && canCopyAudioItem(activeAudioItem)) {
      dispatch(editorThunks.setClipboard(activeAudioItem))
    }
  })

export const projectDataThunks = {
  ...actions,
  ...projectDataAudioThunks,
  ...projectDataMediaThunks,
  ...projectDataHistoryThunks,
  ...projectDataProcessingItemsThunks,
  getProjectData,
  patchProjectData,
  splitTimelineItem,
  removeTimelineSelectedItems,
  copyTimelineItem
}
